import React, { memo, useCallback } from "react";
import { ContactInfoStepProps } from "./types";
import { classNames } from "helpers";
import { TextField } from "ui/components/TextField/TextField";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import OibDropdown from "ui/components/Dropdown/OibDropdown/OibDropdown";
import ContactGroupDropdown from "ui/components/Dropdown/ContactGroupDropdown/ContactGroupDropdown";
import { initialContactTypes } from "services/api/contacts/consts";
import { CardSelect, CardSelectOption } from "ui/components/CardSelect";
import {
  useHandleMultiDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { SudRegResponse } from "services/api/contacts/types";

const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
  showContactType = true,
}) => {
  const [contactTypes] = React.useState(initialContactTypes);

  // Tvrtka je prva: većina kontakata su pravne osobe (dobavljači, servisi, kupci-tvrtke).
  const contactTypeOptions: CardSelectOption[] = [...contactTypes]
    .sort((a, _b) => (a.id === "C" ? -1 : 1))
    .map((ct) => ({
      id: ct.id,
      icon: `/media/icons/${ct.id === "C" ? "company" : "contact"}.svg`,
      title: ct.name,
      description: ct.id === "C" ? "Pravna osoba ili tvrtka" : "Fizička osoba",
    }));

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleMultiDropdownChange = useHandleMultiDropdownChange(setFormValues, formValues);

  const handleOibSelect = useCallback(
    (oib: string, sudRegData: SudRegResponse) => {
      setFormValues((prev) => ({
        ...prev,
        personalIdentificationNumber: oib,
        companyName: sudRegData.name ?? prev.companyName,
        contactTypeId: "C",
      }));
    },
    []
  );

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Detalji kontakta</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Odaberite tip kontakta i unesite osnovne podatke.
          Nakon unosa možete nastaviti na sljedeći korak.
        </p>
        <div className="border border-slate-200 ">
          {showContactType && (
            <div className=" border-b border-slate-200  py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Tip kontakta</h3>
                  <p className="text-xs text-slate-500">Odaberite radi li se o tvrtci ili osobi</p>
              </div>
              <CardSelect
                options={contactTypeOptions}
                value={formValues.contactTypeId}
                onChange={(id) => {
                  setFormValues((prev) => ({ ...prev, contactTypeId: id }));
                  setErrors({});
                }}
                error={!formValues.contactTypeId ? errors.contactTypeId : undefined}
              />
            </div>
          )}

          {formValues.contactTypeId && (formValues.contactTypeId === "C" ? (
            <div>
              {/* Osnovni podaci */}
              <div className=" border-b border-slate-200  py-6 px-4">
                <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Osnovni podaci</h3>
                    <p className="text-xs text-slate-500">Unesite osnovne podatke o tvrtci</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <OibDropdown
                      name="personalIdentificationNumber"
                      label="OIB"
                      error={errors.personalIdentificationNumber}
                      value={formValues.personalIdentificationNumber}
                      required
                      onSudRegSelect={handleOibSelect}
                    />
                  </div>
                  <div className="col-span-2">
                    <TextField
                      type="text"
                      name="companyName"
                      label="Naziv tvrtke"
                      placeholder="Unesite naziv tvrtke"
                      onChange={handleTextFieldChange}
                      error={errors.companyName}
                      value={formValues.companyName}
                      required
                      endIcon=""
                    />
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <>
              {/* Osobni podaci */}
              <div className=" border-b border-slate-200 py-6 px-4">
                <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Osobni podaci</h3>
                    <p className="text-xs text-slate-500">Unesite osobne podatke kontakta</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <TextField
                      type="text"
                      name="firstName"
                      label="Ime"
                      placeholder="Unesite ime kontakta"
                      onChange={handleTextFieldChange}
                      error={errors.firstName}
                      value={formValues.firstName}
                      required
                      endIcon=""
                    />
                  </div>
                  <div>
                    <TextField
                      type="text"
                      name="lastName"
                      label="Prezime"
                      placeholder="Unesite prezime kontakta"
                      onChange={handleTextFieldChange}
                      error={errors.lastName}
                      value={formValues.lastName}
                      required
                      endIcon=""
                    />
                  </div>
                  <div>
                    <OibTextField
                      type="text"
                      name="personalIdentificationNumber"
                      label="OIB"
                      onChange={handleTextFieldChange}
                      error={errors.personalIdentificationNumber}
                      value={formValues.personalIdentificationNumber}
                      required
                    />
                  </div>
                </div>
              </div>

            </>
          ))}

          {/* Poslovno - samo za osobe */}
          <div className={classNames("border-b border-slate-200  py-6 px-4", formValues.contactTypeId !== "P" && "hidden")}>
            <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Poslovno</h3>
                <p className="text-xs text-slate-500">Unesite poslovne podatke kontakta</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <TextField
                  type="text"
                  name="jobTitle"
                  label="Funkcija"
                  placeholder="Unesite funkcije"
                  onChange={handleTextFieldChange}
                  error={errors.jobTitle}
                  value={formValues.jobTitle}
                  required
                  endIcon=""
                />
              </div>
              <div>
                <TextField
                  type="text"
                  name="department"
                  label="Odjel"
                  placeholder="Unesite odjel"
                  onChange={handleTextFieldChange}
                  error={errors.department}
                  value={formValues.department}
                  required
                  endIcon=""
                />
              </div>
              <div></div>
            </div>
          </div>

          {/* Grupe - uvijek vidljive */}
          <div className=" py-6 px-4">
            <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Grupe</h3>
                <p className="text-xs text-slate-500">Odaberite grupe kojima kontakt pripada</p>
            </div>
            {/* Grupa ide preko cijele širine — odabranih zna biti više, a svaka je oznaka
                zasebna pločica, pa ih u trećini retka nema gdje smjestiti. */}
            <ContactGroupDropdown
              placeholder="Pretraži grupe kontakata"
              label="Grupa"
              name="contactGroupIds"
              value={formValues.contactGroupIds}
              contactTypeId={formValues.contactTypeId}
              onChange={handleMultiDropdownChange}
              width="full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ContactInfoStep);
